import { Form, Formik, type FormikHelpers } from 'formik';
import { useUpdateHouseMutation } from 'gcs-common/clients/api/entities/houses/houses-api';
import { convertMessageDeToEn, convertMessageEnToDe } from 'gcs-common/helper/HouseCustomMessageHelper';
import {
  getCustomMessageTypes,
  getExampleCustomMessage,
  getIsValidatingHouseCustomMessage,
  getSelectedHouseCustomMessages,
  getSelectedHouseDetails,
  getValidateHouseCustomMessageErrorMessage,
} from 'gcs-common/slices/administration/houses/housesSelectors';
import {
  resetExampleCustomMessage,
  resetValidateHouseCustomMessageErrorMessage,
} from 'gcs-common/slices/administration/houses/housesSlice';
import {
  fetchHouseCustomMessageTypes,
  validateHouseCustomMessage,
} from 'gcs-common/slices/administration/houses/housesThunks';
import { type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';
import CheckIcon from '../../../../img/check_green.svg?react';
import CancelIcon from '../../../../img/close_red.svg?react';
import EditIcon from '../../../../img/edit.svg?react';
import { showFailureNotification } from '../../../../slices/notifications/ShowNotificationThunk';
import FormikInput from '../../../FormikInput/FormikInput';
import FormikSelect from '../../../FormikSelect/FormikSelect';
import Highlight from '../../../Highlight/Highlight';
import validateCustomMessage, { MAX_MESSAGE_LENGTH } from '../HouseCreateValidationHelper/HouseCreateValidationHelper';
import styles from './styles.module.scss';

Yup.addMethod(Yup.object, 'validateCustomMessage', validateCustomMessage);

type MessageTypeOption = {
  label: string;
  value: string;
};

const HouseCustomMessages = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);

  const validationErrorMessage = useSelector(getValidateHouseCustomMessageErrorMessage);

  const isValidatingCustomMessage = useSelector(getIsValidatingHouseCustomMessage);
  const houseCustomMessages = useSelector(getSelectedHouseCustomMessages);
  const exampleCustomMessage = useSelector(getExampleCustomMessage);
  const customMessageTypes = useSelector(getCustomMessageTypes);
  const selectedHouse = useSelector(getSelectedHouseDetails);
  const [selectedMessageType, setSelectedMessageType] = useState<string | undefined>(undefined);
  const [initMessage, setInitMessage] = useState('');
  const [selectedCustomMessageParams, setSelectedCustomMessageParams] = useState<Array<{ key_label: string }>>([]);
  const [message, setMessage] = useState('');
  const dispatch = useDispatch();

  const updateHouseMutation = useUpdateHouseMutation();

  const validationSchema = Yup.object().shape({
    selectedCustomMessageType: Yup.string(),
    // biome-ignore lint/suspicious/noExplicitAny: validateCustomMessage is added via Yup.addMethod and not typed
    customMessages: (Yup.object().shape({}) as any).validateCustomMessage(customMessageTypes || []),
  });

  const startEditing = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsEditing(true);
  }, []);

  const { customMessages } = selectedHouse || {};

  const initialValues = useMemo(() => {
    return {
      customMessages: customMessages || {},
    };
  }, [customMessages]);

  const { message: exampleMessage, parameters: exampleCustomParameters } = exampleCustomMessage || {};

  const customMessageOptions: MessageTypeOption[] = customMessageTypes.map((m: { type: string; label: string }) => {
    const { type, label } = m;
    return { value: type, label };
  });

  const cancelEditing = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      dispatch(resetExampleCustomMessage());
      setIsEditing(false);
      setIsCanceled(true);
    },
    [dispatch],
  );

  useEffect(() => {
    if (validationErrorMessage) {
      dispatch(
        showFailureNotification(validationErrorMessage, dispatch(resetValidateHouseCustomMessageErrorMessage())),
      );
    }
  }, [validationErrorMessage, dispatch]);

  useEffect(() => {
    dispatch(resetValidateHouseCustomMessageErrorMessage());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchHouseCustomMessageTypes());
  }, [dispatch]);

  const [validateDebouncedMessage] = useDebouncedCallback(
    async (msg: string, isValid: boolean, isValidating: boolean) => {
      if (!isValidating && isValid) {
        // biome-ignore lint/suspicious/noExplicitAny: untyped Redux thunk dispatch
        await (dispatch as any)(
          // biome-ignore lint/suspicious/noExplicitAny: untyped Redux thunk
          (validateHouseCustomMessage as any)({
            customMessage: msg,
            parameters: selectedCustomMessageParams,
            messageType: selectedMessageType,
            houseId: selectedHouse.id,
          }),
        );
      }
    },
    500,
  );

  const submit = useCallback(
    async (values: typeof initialValues, { setSubmitting }: FormikHelpers<typeof initialValues>) => {
      try {
        await updateHouseMutation.mutateAsync(
          {
            path: {
              house_id: selectedHouse.id,
            },
            body: values,
          },
          {
            onSuccess: () => {
              setIsEditing(false);
            },
            onSettled: () => {
              setSubmitting(false);
            },
          },
        );
      } catch {
        // Error toast is handled by the mutation onError callback.
      }
    },
    [selectedHouse, updateHouseMutation],
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={submit}
      validateOnChange
    >
      {({ isSubmitting, resetForm, isValidating, isValid, values, setValues }) => (
        <Form noValidate>
          <div className={styles.bottomHeader}>
            <div className={styles.buttonsWrapper}>
              {isEditing ? (
                <>
                  <button
                    className={styles.saveButton}
                    type="submit"
                    disabled={isSubmitting || isValidatingCustomMessage}
                  >
                    <CheckIcon />
                  </button>
                  <button
                    className={styles.saveButton}
                    type="button"
                    onClick={(event) => {
                      resetForm();
                      cancelEditing(event);
                    }}
                    disabled={isSubmitting}
                  >
                    <CancelIcon />
                  </button>
                </>
              ) : (
                <button className={styles.saveButton} type="button" onClick={startEditing}>
                  <EditIcon />
                </button>
              )}
            </div>
          </div>
          <FormikSelect
            label="Wählen Sie eine Nachricht"
            name="messageType"
            placeholder="Bitte wählen"
            options={customMessageOptions}
            labelKey="label"
            valueKey="value"
            onChange={(msgType: string) => {
              if (selectedMessageType && !isValid) {
                setValues(() => ({
                  ...values,
                  messageType: msgType,
                  customMessages: {
                    ...values.customMessages,
                    [msgType]: initialValues.customMessages[msgType],
                  },
                }));
              }
              dispatch(resetExampleCustomMessage());
              setSelectedMessageType(msgType);
              const customMessageType = customMessageTypes.find((m: { type: string }) => m.type === msgType);
              setSelectedCustomMessageParams(customMessageType.parameters);
              const { [msgType]: msg = customMessageType.default } = houseCustomMessages || {};
              const germanMessage = convertMessageEnToDe(msg, customMessageType.parameters);
              setInitMessage(germanMessage);
              setMessage(germanMessage);
              if (isEditing && !isValidating && isValid) {
                // biome-ignore lint/suspicious/noExplicitAny: untyped Redux thunk dispatch
                (dispatch as any)(
                  // biome-ignore lint/suspicious/noExplicitAny: untyped Redux thunk
                  (validateHouseCustomMessage as any)({
                    customMessage: msg,
                    parameters: customMessageType.parameters,
                    messageType: msgType,
                    houseId: selectedHouse.id,
                  }),
                );
              }
            }}
            isClearable={false}
          />
          {selectedMessageType && (
            <div className={styles.formikInputWrapper}>
              <FormikInput
                as="textarea"
                textClassName={`${styles.formikInput} ${isEditing ? styles.formikInputEdit : ''}`}
                errorClassName={styles.formikError}
                disabled={!isEditing}
                name="customMessages"
                disableHoverOver
                value={isCanceled ? initMessage : message}
                onChange={(event: { target: { value: string } }) => {
                  const msg = event.target.value;
                  setMessage(msg);
                  const customMessageEn = convertMessageDeToEn(msg, selectedCustomMessageParams);
                  setIsCanceled(false);
                  setValues(() => ({
                    ...values,
                    messageType: selectedMessageType,
                    customMessages: {
                      ...values.customMessages,
                      [selectedMessageType as string]: customMessageEn,
                    },
                  }));
                  validateDebouncedMessage(msg, isValid, isValidating);
                }}
                maxLength={MAX_MESSAGE_LENGTH}
              >
                {isEditing && (
                  <div className={styles.formikInfoWrapper}>
                    <div className={styles.formikInfoLarge}>
                      {'Sie können die folgende Parameter in die automatischen Meldungen aufnehmen.' +
                        '\n Bitte fügen Sie die Klammern {} hinzu,' +
                        ' wenn sie die Parameter verwenden.'}
                    </div>
                    <div className={`${styles.formikInfoLarge} ${styles.bold}`}>
                      {selectedCustomMessageParams.map((p) => `{${p.key_label}}`).join(', ')}
                    </div>
                  </div>
                )}
                {isEditing && exampleMessage && (
                  <div className={styles.exampleMessageWrapper}>
                    <div className={styles.exampleMessageHeader}>Beispielnachricht</div>
                    <div className={styles.formikInfoLarge}>
                      <Highlight search={exampleCustomParameters}>{exampleMessage}</Highlight>
                    </div>
                  </div>
                )}
              </FormikInput>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default HouseCustomMessages;
